import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved, faCheck, faFlag, faEye, faSpinner,
  faChevronLeft, faChevronRight, faExclamationTriangle,
  faCheckCircle, faFilter,
} from "@fortawesome/free-solid-svg-icons";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-400" },
  reviewed: { label: "Reviewed", color: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-400" },
  actioned: { label: "Actioned", color: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-400" },
};

const ageHours = (d) => {
  if (!d) return null;
  return Math.round((Date.now() - new Date(d).getTime()) / 3600000);
};

const PhotoModeration = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actioning, setActioning] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const limit = 20;

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const [reportsRes, statsRes] = await Promise.all([
        func.get("mst-admin/photo-reports", { params: { status: statusFilter, page, limit } }),
        func.get("mst-admin/photo-reports/stats"),
      ]);
      if (reportsRes?.status === 200) {
        setReports(reportsRes.data.reports ?? []);
        setTotalPages(reportsRes.data.totalPages ?? 1);
        setTotal(reportsRes.data.total ?? 0);
      }
      if (statsRes?.status === 200) {
        setStats(statsRes.data);
      }
    } catch {
      toast.error("Failed to load photo reports");
    }
    setLoading(false);
  }, [statusFilter, page]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const handleAction = async (id, status) => {
    setActioning(id);
    try {
      const res = await func.put(`mst-admin/photo-reports/${id}`, { status });
      if (res?.status === 200) {
        toast.success(`Report marked as ${status}`);
        setReports(prev => prev.map(r => r._id === id ? { ...r, status } : r));
      } else {
        toast.error("Action failed");
      }
    } catch {
      toast.error("Action failed");
    }
    setActioning(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-700 to-rose-600 px-6 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <FontAwesomeIcon icon={faShieldHalved} className="text-white text-lg" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Photo Moderation</h1>
              <p className="text-red-200 text-xs">User-reported photos awaiting review</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            {[
              {
                label: "Pending Review", value: stats?.byStatus?.pending ?? "—",
                urgent: (stats?.byStatus?.pending ?? 0) > 0,
              },
              { label: "Reviewed", value: stats?.byStatus?.reviewed ?? "—" },
              { label: "Actioned", value: stats?.byStatus?.actioned ?? "—" },
              {
                label: "Overdue (>24h)", value: stats?.overdueReports ?? "—",
                urgent: (stats?.overdueReports ?? 0) > 0,
              },
            ].map(s => (
              <div key={s.label}
                className={`rounded-xl px-4 py-3 border ${
                  s.urgent ? "bg-red-900/30 border-red-400/30" : "bg-white/15 border-white/20"
                }`}>
                <p className="text-red-200 text-xs">{s.label}</p>
                <p className={`text-2xl font-bold mt-0.5 ${s.urgent ? "text-red-200" : "text-white"}`}>
                  {loading ? "—" : s.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Top reason breakdown */}
        {stats?.byReason?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-5">
            <h2 className="font-semibold text-slate-800 mb-4 text-sm">Report Reasons</h2>
            <div className="flex flex-wrap gap-2">
              {stats.byReason.map((r, i) => (
                <span key={i} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                  {r._id} · <span className="font-bold">{r.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Filter + List */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-50">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faFilter} className="text-slate-400 text-xs" />
              <div className="flex gap-1">
                {["pending", "reviewed", "actioned", "all"].map(s => (
                  <button
                    key={s}
                    onClick={() => { setStatusFilter(s); setPage(1); }}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                      statusFilter === s
                        ? "bg-indigo-600 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                    {s === "pending" && (stats?.byStatus?.pending ?? 0) > 0 && (
                      <span className="ml-1.5 bg-red-500 text-white text-[10px] px-1.5 rounded-full">
                        {stats.byStatus.pending}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <span className="text-xs text-slate-400">{fmt(total)} reports</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-slate-300" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-16">
              <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-emerald-300 mb-3" />
              <p className="text-slate-400 text-sm">No {statusFilter} reports</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {reports.map((report) => {
                const hours = ageHours(report.createdAt);
                const isOverdue = hours != null && hours > 24 && report.status === "pending";
                const isExpanded = expanded === report._id;
                return (
                  <div key={report._id} className={`px-5 py-4 ${isOverdue ? "bg-red-50/40" : "hover:bg-slate-50"} transition`}>
                    <div className="flex items-start gap-4">
                      {/* Photo */}
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 shrink-0 border border-slate-200 cursor-pointer"
                        onClick={() => setExpanded(isExpanded ? null : report._id)}>
                        <img
                          src={report.photoUrl}
                          alt="Reported"
                          className="w-full h-full object-cover"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_CONFIG[report.status]?.color}`}>
                                {STATUS_CONFIG[report.status]?.label}
                              </span>
                              {isOverdue && (
                                <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-[10px]" />
                                  Overdue {hours}h
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-slate-800">{report.reason}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              Reported by: {report.reportedBy?._phoneNumber || "Unknown"} ·
                              Profile: {report.bgProfile?.firstName || report.profileId?._phoneNumber || "Unknown"} ({report.bgProfile?.gender || "—"}) ·
                              {fmtDate(report.createdAt)} {fmtTime(report.createdAt)}
                            </p>
                          </div>

                          {report.status === "pending" && (
                            <div className="flex gap-2 shrink-0">
                              <button
                                onClick={() => handleAction(report._id, "reviewed")}
                                disabled={actioning === report._id}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                              >
                                <FontAwesomeIcon icon={actioning === report._id ? faSpinner : faEye} spin={actioning === report._id} />
                                Review
                              </button>
                              <button
                                onClick={() => handleAction(report._id, "actioned")}
                                disabled={actioning === report._id}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                              >
                                <FontAwesomeIcon icon={actioning === report._id ? faSpinner : faFlag} spin={actioning === report._id} />
                                Action
                              </button>
                            </div>
                          )}
                          {report.status === "reviewed" && (
                            <button
                              onClick={() => handleAction(report._id, "actioned")}
                              disabled={actioning === report._id}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 shrink-0"
                            >
                              <FontAwesomeIcon icon={actioning === report._id ? faSpinner : faFlag} spin={actioning === report._id} />
                              Take Action
                            </button>
                          )}
                        </div>

                        {/* Expanded photo preview */}
                        {isExpanded && (
                          <div className="mt-3">
                            <img
                              src={report.photoUrl}
                              alt="Full size preview"
                              className="max-h-64 rounded-xl border border-slate-200 object-contain"
                              onError={e => { e.target.parentElement.innerHTML = '<p class="text-xs text-slate-400">Image unavailable</p>'; }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 px-5 py-4 border-t border-slate-50">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 flex items-center justify-center">
                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
              </button>
              <span className="text-xs text-slate-500">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 flex items-center justify-center">
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoModeration;
